import {OSkinableInit} from "/@back@/commons/basis.js";
import {AgBoxErrBarEditor, OWedEditorErrBarConfig} from "/@back@/edit/wed/features/boxErrBar.js";
import {AgBoxSelEditor, IWedEditorBoxSel} from "/@back@/edit/wed/features/boxSel.js";
import {AgCommonBarEditor, IWedEditorCommonBar, OWedEditorCommonBarConfig} from "/@back@/edit/wed/features/commonBar.js";
import {AgInsSelEditor} from "/@back@/edit/wed/features/insMgr.js";
import {AgMetaBarEditor, IWedEditorMetaBar, OWedEditorMetaConfig} from "/@back@/edit/wed/features/metaBar.js";
import {AgOutlineBarEditor, IWedEditorOutlineBar, OWedEditorOutlineConfig} from "/@back@/edit/wed/features/outlineBar.js";
import {AgPreviewBarEditor, IWedEditorPreviewBar, OWedEditorPreviewConfig} from "/@back@/edit/wed/features/previewBar.js";
import {AgTxtSelEditor} from "/@back@/edit/wed/features/txtSel.js";
import {WED} from "/@back@/edit/wed/wedCore.js";
import {IWedEdBar, IWedEditor, IWedEditorBarLayout, IWedEditorMain, OWedEditorConfig, OWedManagerConfig, WedEditorBase, WedMgr, WedPanelBar} from "/@back@/edit/wed/wedEditor.js";
import {WED_SELECTOR_BOX} from "/@back@/edit/wed/wedlets/box/box.js";
import {Action} from "/@lib@/commons/actions.js";
import {IEndPoint, IPathResolver} from "/@lib@/commons/io/io.js";
import {ILastDatasBuilder, JLastDatas} from "/@lib@/commons/lastDatas.js";
import {IReg, IRegPointer, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {VIEWS} from "/@lib@/commons/views.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {IDocHolder} from "/@lib@/edit/docHolder.js";
import {ErrorMsg} from "/@lib@/edit/ot/urban.js";
import {Schema} from "/@lib@/edit/schema/schema.js";
import {RelaxNgSchemaBuilder} from "/@lib@/edit/schema/schemaBuilder.js";
import {SkMetaLib} from "/@lib@/edit/schema/schemaMeta.js";
import {Resizer} from "/@back@/commons/widgets/resizer.js";
import {AgSearchBarEditor} from "/@back@/edit/wed/features/searchBar.js";
import {AgDiffBarEditor} from "/@back@/edit/wed/features/diffBar.js";
import {markAsScrollCtn} from "/@back@/edit/wed/wedlets/wedlet.js";

export interface OWedEditorBoxConfig extends OWedEditorPreviewConfig, OWedEditorOutlineConfig, OWedEditorMetaConfig, OWedEditorCommonBarConfig, OWedEditorErrBarConfig, OSkinableInit {
	reg?: IReg<IUiEnv>
	disableBoxSelection?: boolean
	disableVirtuals?: boolean
	disableDefaultAccelKeys?: boolean
	customAccelKeysList?: string
}

//Pour les méthodes ajoutées en Agrégations.
export interface WedEditorBox extends IWedEditorPreviewBar, IWedEditorOutlineBar, IWedEditorCommonBar, IWedEditorMetaBar {

	reg: IReg<IUiEnv>;

	initialize(init: OWedEditorBoxConfig): this
}

export class WedEditorBox extends WedEditorBase implements IWedEditorBoxSel, IWedEditorPreviewBar, IWedEditorOutlineBar, IWedEditorCommonBar, IWedEditorMetaBar, ILastDatasBuilder {

	config: OWedEditorBoxConfig;

	schema: Schema;

	barLayout: IWedEditorBarLayout;

	protected _loadings: Promise<any>[];
	protected _inSync: Promise<any>;

	protected initUi(init: OWedEditorBoxConfig) {
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.barLayout = init.barLayout || new FullLayout();
		this.barLayout.initLayout(this, this.reg);
		this.scrollContainer = this.shadowRoot.getElementById("scrollBox");
		if (this.scrollContainer) markAsScrollCtn(this.scrollContainer);
		if (!init.disableDefaultAccelKeys) {
			if (init.customAccelKeysList) {
				this.wedMgr.accelKeyMgr.initFromMapActions(this.reg.mergeListsAsMap<Action<IWedEditor>>(init.customAccelKeysList, "accelKeys:wed:global"));
			} else {
				this.wedMgr.accelKeyMgr.initFromMapActions(this.reg.getListAsMap<Action<IWedEditor>>("accelKeys:wed:global"));
			}
		} else if (init.customAccelKeysList) {
			this.wedMgr.accelKeyMgr.initFromMapActions(this.reg.getListAsMap<Action<IWedEditor>>(init.customAccelKeysList));
		}
	}

	/**
	 * Permet de déclarer une librairie de composants qui se charge en asynchrone.
	 * L'éditeur ne sera effectivement construit que lorsque ces librairies seront chargées.
	 */
	addAsyncLib(lib: Promise<any>): Promise<any> {
		if (this._loadings == null) {
			this._loadings = [];
			this._inSync = null;
		}
		this._loadings.push(lib);
		return lib;
	}

	/** Promise résolue lorsque les différents processus d'initialisation préalablement initiés sont achevés.*/
	get inSync(): Promise<any> {
		if (!this._inSync) {
			this._inSync = this._loadings ? Promise.all(this._loadings) : Promise.resolve();
		}
		return this._inSync;
	}

	get accelKeyMgr() {
		return this.wedMgr?.accelKeyMgr
	}

	/** Configuration du WedModel. */
	setWedModelEndPoint(endPoint: IEndPoint, resolver?: IPathResolver): Promise<any> {
		return this.addAsyncLib(
			WED.loadWedModel(endPoint, resolver).then(wedModel => {this.wedModel = wedModel;})
		);
	}

	/** Configuration du Schéma RelaxNg. */
	setRelaxNG(endPoint: IEndPoint, init?: RequestInit, skMetaLib?: SkMetaLib): Promise<any> {
		return this.addAsyncLib(
			//TODO regsitre static central des RNG ?
			new RelaxNgSchemaBuilder(skMetaLib).buildRelaxNgFromUrl(endPoint, init).then(rngSchema => {this.schema = rngSchema})
		);
	}

	// async initFromEndPoint(endPoint: IEndPoint, init?: RequestInit, config?: OWedManagerConfig): Promise<boolean> {
	// 	return this.initFromDocument(await endPoint.fetchDom("", init), config);
	// }
	//
	// initFromXml(xml: string, config?: OWedManagerConfig, baseUrl?: IEndPoint): Promise<boolean> {
	// 	return this.initFromDocument(DOM.parseDom(xml, baseUrl), config);
	// }

	// initFromDocument(doc: Document, config?: OWedManagerConfig): Promise<boolean> {
	// 	return this.inSync.then(() => {
	// 		if (!this.schema) throw Error("No Schema declared");
	// 		return this.initFromDocHolder(createDocHolderLocal(this.schema, DOM.cleanupDom(doc, true, false, true), {autoMutate: true, autoComplete: true, autoNormXml: true, autoNormChars: true, genAnnots: true}), config);
	// 	});
	// }

	initFromDocHolder(docHolder: IDocHolder, config: OWedManagerConfig = {}): Promise<boolean> {
		this.clearContent();
		return this.inSync.then(() => {
			if (!this.wedModel) throw Error("No wedModel declared");
			if (!config.wedRootModelSelector) config.wedRootModelSelector = WED_SELECTOR_BOX;
			return this.wedMgr.initWedMgr(this.wedModel, docHolder, config);
		});
	}

	clearContent() {
		this.mainContainer.textContent = null;
		//XXX clear metaBar ?
	}

	onMsgError(errMsg: ErrorMsg): 'redraw' | void {
		if (errMsg.error === 'masterDoorClosed') {
			//FIXME
			alert("Connexion avec le serveur intérrompue");
			location.reload();
		} else {
			return 'redraw';
		}
	}
}

AgBoxSelEditor(AgTxtSelEditor(AgCommonBarEditor(AgMetaBarEditor(AgOutlineBarEditor(AgPreviewBarEditor(AgBoxErrBarEditor(AgDiffBarEditor(AgSearchBarEditor(WedEditorBox)))))))));
AgInsSelEditor(WedEditorBox, {mainBranch: true, includeVirtuals: true});


REG.reg.registerSkin('box-editor/full', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	[hidden] {
		display: none !important;
	}

	.v {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	.h {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: row;
	}

	c-resizer[c-orient=row] {
		width: 1px;
	}

	c-resizer[c-orient=column] {
		height: 1px;
	}

	.ctn {
		background-color: var(--edit-bgcolor);
		color: var(--edit-color);
	}

	#endBox {
		flex: 1 1 30%;
		min-width: 10%;
	}

	#metaBox {
	  flex: 1 1 70%;
	  min-width: 10%;
  }

  #previewPanel,
  #outlinePanel {
	  flex: 1 1 50%;
	  overflow: auto;
  }

  #l10nDrvBar {
	  flex: 1 1 50%;
  }

  #scrollBox {
	  flex: 1 1 80%;
	  min-width: 10%;
	  overflow-y: auto;
	  scrollbar-gutter: stable;
	  min-height: 3em;
	  scroll-behavior: smooth;
  }

  #metaPanel {
		flex: 1 1 20%;
		min-width: 10%;
	}

	#commonBar,
	#diffBar {
		border-bottom: 1px solid var(--border-color);
		min-height: 1.8em;
	}

	#txtStackBar,
	#searchBar,
	#unknownBar {
		border-top: 1px solid var(--border-color);
	}

	#errorBar {
		border-inline-start: 1px solid var(--border-color);
	}
`);

customElements.define('box-editor', WedEditorBox);

/**
 * Sous box editor pour la visualisation de fragments décrochés de l'arbre principal :
 * - BoxScExclude
 * - WedDiffForeign
 *
 * La gestion de la sélection Box et Txt sont activées notamment pour pouvoir copier du contenu.
 */
export class WedEditorFrag extends WedEditorBase implements CustomElement, IWedEdBar {
	scrollContainer: HTMLElement;

	initialize(init: OWedEditorConfig): this {
		if (init.crossNav === undefined) init.crossNav = true;
		super.initialize(init);
		return this;
	}
}

AgBoxSelEditor(AgTxtSelEditor(AgCommonBarEditor(WedEditorFrag)));

customElements.define("box-editor-frag", WedEditorFrag);

/**
 * Layout par défaut gérant le positionnement des bars:
 * - commonBar
 * - errorBar
 * - outlineBar
 * - previewBar
 * - metaBar
 */
class FullLayout implements IWedEditorBarLayout {
	editor: IWedEditorMain;

	initLayout(editor: IWedEditorMain, reg: IReg<IUiEnv>): void {
		this.editor = editor;
		//editor.style.overflow = "hidden"; //obligatoire pour que le scroll horizontal fonctionne bien. cf https://stackoverflow.com/a/35609992/8691603
		const sr = editor.shadowRoot;
		reg.installSkin('box-editor/full', sr);
		reg.installSkin('scroll/large', sr);
		editor.mainContainer = <div id={editor.config.withoutScrolls ? "rootBox" : "scrollBox"} class="ctn" c-resizable=""/>
		editor.rootNode = <div id="metaBox" class="v" c-resizable="">
			{editor.mainContainer}
		</div> as HTMLElement & IRegPointer<any>;
		sr.appendChild(<div id="colBox" class="v">
			<div id="rowBox" class="h">
				{editor.rootNode}
				<div id="endBox" class="v" hidden c-resizable=""/>
			</div>
		</div>);
		editor.wedMgr.listeners.on("buildLastDatas", function (this: void, wedMgr: WedMgr, lastDatas: JLastDatas) {
			const sr = wedMgr.wedEditor.shadowRoot;
			const endBox = sr.getElementById('endBox');
			if (!endBox.hidden) {
				lastDatas.lytEndBox = endBox.style.flexBasis;
				lastDatas.lytMetaBox = sr.getElementById('metaBox').style.flexBasis;
				const otl = sr.getElementById('outlinePanel');
				if (otl && !otl.hidden) lastDatas.lytOtl = otl.style.flexBasis;
				const prv = sr.getElementById('previewPanel');
				if (prv && !prv.hidden) lastDatas.lytPrv = prv.style.flexBasis;
			}
		});
		editor.wedMgr.listeners.on("initLastDatas", function (this: void, wedMgr: WedMgr, lastDatas: JLastDatas) {
			const sr = wedMgr.wedEditor.shadowRoot;
			if (lastDatas.lytEndBox) {
				sr.getElementById('endBox').style.flexBasis = lastDatas.lytEndBox;
				if (lastDatas.lytMetaBox) sr.getElementById('metaBox').style.flexBasis = lastDatas.lytMetaBox;
				if (lastDatas.lytOtl) {
					const panel = sr.getElementById('outlinePanel');
					if (panel) panel.style.flexBasis = lastDatas.lytOtl;
				}
				if (lastDatas.lytPrv) {
					const panel = sr.getElementById('previewPanel');
					if (panel) panel.style.flexBasis = lastDatas.lytPrv;
				}
			}
		});
	}

	getBar(id: string): IWedEdBar {
		return this.editor.shadowRoot.getElementById(id);
	}

	showBar(bar: IWedEdBar): boolean {
		let ctn: HTMLElement;
		switch (bar.id) {
		case 'commonBar' :
			ctn = this.editor.shadowRoot.getElementById("colBox");
			if (bar.parentNode !== ctn) ctn.insertBefore(bar, ctn.firstChild);
			break;
		case 'errorBar' :
			ctn = this.editor.shadowRoot.getElementById("rowBox");
			if (bar.parentNode !== ctn) ctn.appendChild(bar);
			break;
		case 'outlineBar' :
			ctn = this.editor.shadowRoot.getElementById("endBox");
			let outlinePanel = DOMSH.findHost(bar) as WedPanelBar;
			if (!outlinePanel) outlinePanel = ctn.insertBefore(<WedPanelBar î={{barBody: bar}} id="outlinePanel" c-resizable=""/>, ctn.firstChild) as WedPanelBar;
			else DOM.setHidden(outlinePanel, false);
			this.refreshEndBox(ctn);
			break;
		case 'previewBar' :
			ctn = this.editor.shadowRoot.getElementById("endBox");
			let previewPanel = DOMSH.findHost(bar) as WedPanelBar;
			if (!previewPanel) previewPanel = ctn.appendChild(<WedPanelBar î={{barBody: bar}} id="previewPanel" c-resizable=""/>) as WedPanelBar;
			else DOM.setHidden(previewPanel, false);
			this.refreshEndBox(ctn);
			break;
		case 'metaBar' :
			ctn = this.editor.shadowRoot.getElementById("metaBox");
			let metaPanel = DOMSH.findHost(bar) as WedPanelBar;
			if (!metaPanel) {
				ctn.appendChild(<c-resizer c-orient="column"/>);
				metaPanel = ctn.appendChild(<WedPanelBar î={{barBody: bar}} id="metaPanel" c-resizable=""/>) as WedPanelBar;
			} else {
				DOM.setHidden(metaPanel, false);
				DOM.setHidden(metaPanel.previousElementSibling, false); //show c-resizer
			}
			this.refreshEndBox(ctn);
			break;
		case 'txtStackBar':
			//1ère barre en dessous de la zone principale (rowBox)
			const prev = this.editor.shadowRoot.getElementById("rowBox");
			ctn = prev.parentElement;
			if (bar.parentNode !== ctn) ctn.insertBefore(bar, prev ? prev.nextSibling : null);
			break;
		case 'searchBar':
			//barre sous txtStackBar mais au dessus de unknownBar
			const unknownBar = this.editor.shadowRoot.getElementById("unknownBar");
			ctn = this.editor.shadowRoot.getElementById("colBox");
			if (bar.parentNode !== ctn) ctn.insertBefore(bar, unknownBar && unknownBar.parentNode === ctn ? unknownBar : null);
			break;
		case 'diffBar':
			//barre sous commonBar
			const rowBox = this.editor.shadowRoot.getElementById("rowBox");
			ctn = rowBox.parentElement;
			if (bar.parentNode !== ctn) ctn.insertBefore(bar, rowBox);
			break;
		case 'l10nDrvBar' :
			ctn = this.editor.shadowRoot.getElementById("rowBox");
			if (bar.parentNode !== ctn) {
				ctn.appendChild(<c-resizer c-orient="row"/>);
				bar.setAttribute("c-resizable", "");
				ctn.appendChild(bar);
			} else {
				DOM.setHidden(bar.previousElementSibling, false); //show c-resizer
			}
			break;
		case 'unknownBar':
			//Barre d'alerte d'éléments inconnus (associée à la boxErrBar).
			//Dernière barre en dessous de la zone principale
			ctn = this.editor.shadowRoot.getElementById("colBox");
			if (bar.parentNode !== ctn) ctn.appendChild(bar);
			break;
		case 'helpBar' :
			ctn = this.editor.shadowRoot.getElementById("rowBox");
			if (bar.parentNode !== ctn) {
				ctn.appendChild(<c-resizer c-orient="row"/>);
				bar.setAttribute("c-resizable", "");
				ctn.appendChild(bar);
			} else {
				DOM.setHidden(bar.previousElementSibling, false); //show c-resizer
			}
			break;
		default:
			console.warn("Bar unknown by this FullLayout!", this, bar);
			return false;
		}
		VIEWS.onViewShown(bar);
		this.editor.wedMgr.listeners.emit("layoutBarChange", this.editor.wedMgr, bar);
		return true;
	}

	onHideBar(bar: IWedEdBar): void {
		const panel = DOMSH.findHost(bar);
		if (panel instanceof WedPanelBar && DOM.setHidden(panel, true)) {
			const ctn = panel.parentElement;
			if (!ctn) return;
			if (ctn.id === "endBox") this.refreshEndBox(ctn);
			else if (bar.id === "metaBar") DOM.setHidden(panel.previousElementSibling, true); //hide c-resizer
			VIEWS.onViewHidden(bar);
		} else if (bar.id === "helpBar") {
			DOM.setHidden(bar.previousElementSibling, true); //hide c-resizer
		}
		this.editor.wedMgr.listeners.emit("layoutBarChange", this.editor.wedMgr, bar);
	}

	removeBar(bar: IWedEdBar): void {
		if (bar.id === 'unknownBar') {
			if (bar.parentNode) bar.remove();
			return;
		} else if (bar.id === 'helpBar') {
			if (bar.parentNode) {
				bar.previousElementSibling.remove(); //remove c-resizer
				bar.remove();
			}
			return;
		}
		const panel = DOMSH.findHost(bar);
		if (panel instanceof WedPanelBar) {
			const ctn = panel.parentElement;
			if (ctn) {
				if (bar.id === "metaBar") panel.previousElementSibling.remove(); //suppr c-resizer
				panel.remove();
				if (ctn.id === "endBox") this.refreshEndBox(ctn);
				VIEWS.onViewHidden(bar, true);
				this.editor.wedMgr.listeners.emit("layoutBarChange", this.editor.wedMgr, bar);
			}
		}
	}

	/** Réévalue l'affichage du endBox et de son resizer et ajuste les resizers dans le endBox. */
	protected refreshEndBox(endBox: HTMLElement) {
		let hidden = true;
		for (let ch = endBox.firstElementChild as HTMLElement; ch; ch = ch.nextElementSibling as HTMLElement) {
			if (!ch.hidden) {
				hidden = false;
				break;
			}
		}
		if (DOM.setHidden(endBox, hidden)) {
			//resizer du endBox
			const prev = endBox.previousElementSibling;
			if (prev) {
				let resizer = prev;
				if (!(resizer instanceof Resizer)) resizer = endBox.parentElement.insertBefore(<c-resizer c-orient="row"/>, endBox);
				DOM.setHidden(resizer, hidden);
			}
		}
		if (!hidden) {
			//adjust c-resizer entre les bars dans le endBox.
			let ch = endBox.firstElementChild;
			let prevVisible = false;
			while (ch) {
				if (!(ch as HTMLElement).hidden) prevVisible = true;
				const next = ch.nextElementSibling;
				if (next instanceof Resizer) {
					ch = next.nextElementSibling;
					DOM.setHidden(next, !prevVisible || (ch as HTMLElement).hidden);
					prevVisible = false;
					continue;
				} else if (next) {
					const resizer = endBox.insertBefore(<c-resizer c-orient="column"/>, next);
					if (!prevVisible || (next as HTMLElement).hidden) resizer.hidden = true;
				}
				prevVisible = false;
				ch = next;
			}
		}
	}

}


export const editorBoxDefined = Promise.resolve();


